import { Injectable } from '@angular/core';
import { AccessLevel, RoleLevel } from '../../core/user/user.model';
import { SyncProvider } from '../../models/integration.model';
import { UserService } from 'app/core/user/user.service';

/**
 * Integration Permission Service
 * Handles permission checks for integration features
 * Updated to use UserService instead of AuthService for permission checks
 */
@Injectable({
  providedIn: 'root',
})
export class IntegrationPermissionService {
  // Provider-specific permission mappings
  private readonly PROVIDER_PERMISSIONS: {
    [key in SyncProvider]: { permission: AccessLevel; client_flag: string };
  } = {
    projects: {
      permission: AccessLevel.SYSTEM_UNIFIER_PROJECT_SYNC,
      client_flag: 'enable_project_unifier',
    },
    p6: {
      // permission: AccessLevel.SYSTEM_UNIFIER_P6_SYNC,
      permission: null,
      client_flag: 'enable_p6_unifier_sync',
    },
    observations: {
      // permission: AccessLevel.SYSTEM_UNIFIER_OBSERVATION_MANAGEMENT,
      permission: null,
      client_flag: 'enable_observation_unifier_sync',
    },
  };

  constructor(private userService: UserService) {}

  hasUnifierPermission(provider: SyncProvider): boolean {
    const user = this.userService.getLoggedInUser();

    if (this.PROVIDER_PERMISSIONS[provider].permission === null) {
      return user[this.PROVIDER_PERMISSIONS[provider].client_flag];
    }

    return (
      this.userService.permissions[this.PROVIDER_PERMISSIONS[provider].permission] &&
      user[this.PROVIDER_PERMISSIONS[provider].client_flag]
    );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Provider-Specific Permission Check Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if user can sync a specific provider
   *
   * @param provider - The provider type
   * @returns boolean
   */
  canSyncProvider(provider: SyncProvider): boolean {
    return this.hasUnifierPermission(provider);
  }

  /**
   * Check if user can test connection for a specific provider
   *
   * @param provider - The provider type
   * @returns boolean
   */
  canTestConnection(provider: SyncProvider): boolean {
    const allowedToTest =
      window.location.hostname === 'stg' ||
      window.location.hostname === 'dev' ||
      window.location.hostname === 'localhost';

    return this.isSuperAdmin() && allowedToTest && this.canSyncProvider(provider);
  }

  /**
   * Get all provider permissions for current user
   *
   * @returns ProviderPermissions
   */
  getProviderPermissions(): ProviderPermissions {
    return {
      projects: this.canSyncProvider('projects'),
      p6: this.canSyncProvider('p6'),
      observations: this.canSyncProvider('observations'),
    };
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Backward Compatible Permission Check Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if user can sync projects
   *
   * @returns boolean
   */
  canSyncProjects(): boolean {
    return this.canSyncProvider('projects');
  }

  /**
   * Check if user can access integration settings
   *
   * @returns boolean
   */
  canAccessIntegrationSettings(): boolean {
    return this.canSyncProvider('projects');
  }

  /**
   * Check if user can view integration dashboard
   *
   * @returns boolean
   */
  canViewIntegrationDashboard(): boolean {
    return this.canSyncProvider('projects');
  }

  /**
   * Check if user can view sync history
   *
   * @returns boolean
   */
  canViewSyncHistory(): boolean {
    return this.canSyncProvider('projects');
  }

  /**
   * Check if user can configure integration settings
   *
   * @returns boolean
   */
  canConfigureIntegration(): boolean {
    return this.isSuperAdmin() && this.canSyncProjects();
  }

  /**
   * Check if user can test integration connection
   *
   * @returns boolean
   */
  canTestIntegrationConnection(): boolean {
    return this.isSuperAdmin() && this.canSyncProjects();
  }

  /**
   * Check if user can view integration statistics
   *
   * @returns boolean
   */
  canViewIntegrationStatistics(): boolean {
    return this.canSyncProvider('projects');
  }

  /**
   * Check if user can view project integration status
   *
   * @returns boolean
   */
  canViewProjectIntegrationStatus(): boolean {
    return this.canSyncProvider('projects');
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Role Check Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if user is Super Admin
   *
   * @returns boolean
   */
  isSuperAdmin(): boolean {
    return this.userService.getLoggedInUser().role_name === RoleLevel.SUPER_ADMIN;
  }

  /**
   * Check if user is Admin
   *
   * @returns boolean
   */
  isAdmin(): boolean {
    return this.userService.getLoggedInUser().role_name === RoleLevel.ADMIN;
  }

  /**
   * Check if user has admin privileges (Super Admin or Admin)
   *
   * @returns boolean
   */
  hasAdminPrivileges(): boolean {
    return this.isSuperAdmin() || this.isAdmin();
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Utility Methods
  // -----------------------------------------------------------------------------------------------------

  /**
   * Check if user has any integration permissions
   *
   * @returns boolean
   */
  hasAnyIntegrationPermissions(): boolean {
    return Object.values(this.getProviderPermissions()).some((permission) => permission);
  }

  /**
   * Get user's integration permission level
   *
   * @returns string
   */
  getIntegrationPermissionLevel(): 'none' | 'basic' | 'admin' | 'super_admin' {
    if (this.isSuperAdmin() && this.hasAnyIntegrationPermissions()) {
      return 'super_admin';
    }

    if (this.isAdmin() && this.hasAnyIntegrationPermissions()) {
      return 'admin';
    }

    if (this.hasAnyIntegrationPermissions()) {
      return 'basic';
    }

    return 'none';
  }

  /**
   * Check if user can perform specific integration action
   *
   * @param action - The action to check
   * @returns boolean
   */
  canPerformAction(action: IntegrationAction): boolean {
    switch (action) {
      case 'sync_projects':
        return this.canSyncProvider('projects');

      case 'sync_p6':
        return this.canSyncProvider('p6');

      case 'sync_observations':
        return this.canSyncProvider('observations');

      case 'view_dashboard':
        return this.canViewIntegrationDashboard();

      case 'view_history':
        return this.canViewSyncHistory();

      case 'configure_settings':
        return this.canConfigureIntegration();

      case 'test_connection':
        return this.canTestIntegrationConnection();

      case 'view_statistics':
        return this.canViewIntegrationStatistics();

      case 'view_project_status':
        return this.canViewProjectIntegrationStatus();

      default:
        return false;
    }
  }

  /**
   * Get list of available actions for current user
   *
   * @returns IntegrationAction[]
   */
  getAvailableActions(): IntegrationAction[] {
    const actions: IntegrationAction[] = [];

    if (this.canSyncProvider('projects')) {
      actions.push('sync_projects');
    }

    if (this.canSyncProvider('p6')) {
      actions.push('sync_p6');
    }

    if (this.canSyncProvider('observations')) {
      actions.push('sync_observations');
    }

    if (this.canViewIntegrationDashboard()) {
      if (this.canSyncProjects()) {
        actions.push('sync_projects');
      }

      if (this.canViewIntegrationDashboard()) {
        actions.push('view_dashboard');
      }

      if (this.canViewSyncHistory()) {
        actions.push('view_history');
      }

      if (this.canConfigureIntegration()) {
        actions.push('configure_settings');
      }

      if (this.canTestIntegrationConnection()) {
        actions.push('test_connection');
      }

      if (this.canViewIntegrationStatistics()) {
        actions.push('view_statistics');
      }

      if (this.canViewProjectIntegrationStatus()) {
        actions.push('view_project_status');
      }

      return actions;
    }

    if (this.canViewSyncHistory()) {
      actions.push('view_history');
    }

    if (this.canConfigureIntegration()) {
      actions.push('configure_settings');
    }

    if (this.canTestIntegrationConnection()) {
      actions.push('test_connection');
    }

    if (this.canViewIntegrationStatistics()) {
      actions.push('view_statistics');
    }

    if (this.canViewProjectIntegrationStatus()) {
      actions.push('view_project_status');
    }

    return actions;
  }
}

/**
 * Provider permissions interface
 */
export interface ProviderPermissions {
  projects: boolean;
  p6: boolean;
  observations: boolean;
}

/**
 * Available integration actions
 */
export type IntegrationAction =
  | 'sync_projects'
  | 'sync_p6'
  | 'sync_observations'
  | 'view_dashboard'
  | 'view_history'
  | 'configure_settings'
  | 'test_connection'
  | 'view_statistics'
  | 'view_project_status';
